import Circle from "@ihs-widget/components/StatsConsole/Widgets/StatsWidget/parts/Circle"
import { truncateString } from "@ihs-widget/utils/functions";
import { useLanguage } from "@ihs-widget/utils/hooks/useLanguage";
import { useEffect, useRef, useMemo, useState } from 'react';
import SmallSpinner from "@ihs-widget/components/common/SmallSpinner";
import { useTranslations } from "../../../../../translation/TranslationContext";
import useTimeoutVisability from "@ihs-widget/utils/hooks/useTimeoutVisability";
import SmallSpinnerTimeout from "@ihs-widget/components/common/SmallSpinnerTimeout";

interface CircleWithLabelsProps {
    label: string;
    home: number;
    away: number;
}

const CircleWithLabels = ({ label, home, away }: CircleWithLabelsProps) => {
    const labelRef = useRef<HTMLDivElement>(null);
    const language = useLanguage();
    const translations = useTranslations(language);
    
    const translatedLabel = useMemo(() => {
        // Proxy in useTranslations guarantees no SC_* key
        const t = translations[label as keyof typeof translations] as unknown as string;
        return typeof t === 'string' ? t : '';
    }, [translations, label]);
    
    const [displayLabel, setDisplayLabel] = useState(translatedLabel);

    // Timeout logic for hiding component when both values are null for 5 seconds
    const bothValuesNull = home === null && away === null;
    const { isVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [bothValuesNull, home, away]
    });

    useEffect(() => {
        const truncateLabel = () => {
            const element = labelRef.current;
            if (!element) return;

            const words = translatedLabel.split(' ');
            let result = '';
            let line = 1;

            for (let i = 0; i < words.length; i++) {
                const testString = result + (result ? ' ' : '') + words[i];
                element.textContent = testString;

                if (element.clientHeight > 32) { 
                    if (line === 2) {
                        result = result.trim() + '...';
                        break;
                    }
                    result += '\n' + words[i];
                    line++;
                } else {
                    result = testString;
                }
            }

            setDisplayLabel(truncateString(result, 29));
        };

        truncateLabel();
        window.addEventListener('resize', truncateLabel);
        return () => window.removeEventListener('resize', truncateLabel);
    }, [translatedLabel]);

    // Hide component if both values are null for 5 seconds
    if (bothValuesNull && !isVisible) {
        return null;
    }

    return (
        <div className="w-[101px] h-full text-base120 dark:text-monoY text-[12px] font-medium font-robotoMedium leading-[16px]">
            <div ref={labelRef} className="h-[32px] w-full flex items-center justify-center align-middle mb-[4px] whitespace-pre-wrap text-center">
                {displayLabel}
            </div>
            <div className="w-full flex items-center justify-center h-[45px]">
                {home !== null ? <div className="text-[16px] font-medium font-robotoMedium leading-[24px] mr-[5px]">{home}</div> : <div className="w-[16px] h-[16px] flex items-center justify-center mr-[5px]"><SmallSpinnerTimeout /></div>}
                <Circle home={home} away={away} />
                {away !== null ? <div className="text-[16px] font-medium font-robotoMedium leading-[24px] ml-[5px]">{away}</div> : <div className="w-[16px] h-[16px] flex items-center justify-center ml-[5px]"><SmallSpinnerTimeout /></div>}
            </div>
        </div>
    )
}

export default CircleWithLabels;

